import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

import { Page } from "@/components/page";
import {
  getPaymentFromSessionStorage,
  type PaymentType,
} from "@/features/payment/utils";
import { orderStatusQuery } from "@/features/wash-start-guide/order-status";
import { WashStartGuide } from "@/features/wash-start-guide/wash-start-guide";
import { getShopFromLocalStorage } from "@/hooks/use-shop";
import { getToken } from "@/hooks/use-token";
import { NTH_PURCHASE_CAMPAIGN_QUERY_KEY } from "@frontend-common/hooks/use-nth-purchase-campaign";

// the machine waits this long for the door + program before we give up
const START_WINDOW_SECONDS = 180;

export const Route = createFileRoute("/wash-start-guide")({
  component: RouteComponent,
  loader: async ({ context }) => {
    // the purchase used up any nth-purchase-campaign hold; don't wait for the next poll to drop the banner
    void context.queryClient.invalidateQueries({
      queryKey: NTH_PURCHASE_CAMPAIGN_QUERY_KEY,
    });
    const payment = getPaymentFromSessionStorage();
    if (!payment?.orderId) {
      throw redirect({ to: "/start-screen", reloadDocument: true });
    }
    const status = await context.queryClient
      .ensureQueryData(orderStatusQuery(payment.orderId))
      .catch(() => null);
    if (!status) {
      throw redirect({ to: "/invoice-question-screen" });
    }
    return {
      payment,
      orderId: payment.orderId,
      startTimeoutSeconds:
        status.startSignalRemainingSeconds ?? START_WINDOW_SECONDS,
      canRequestInvoice: canRequestInvoice(payment),
      currencySymbol: getShopFromLocalStorage()?.currency ?? "Ft",
    };
  },
});

function RouteComponent() {
  const { payment, canRequestInvoice, ...guide } = Route.useLoaderData();
  const navigate = useNavigate();

  // no inactivity modal: the user is at the machine, not the kiosk
  return (
    <Page skipInactvityModal>
      <WashStartGuide
        {...guide}
        onRequestInvoice={
          canRequestInvoice
            ? () =>
                navigate({
                  to: "/invoice-generation-screen/$nonce",
                  params: { nonce: payment.kioskPaymentNonceKey },
                  search: { then: "start" },
                })
            : undefined
        }
        onStartAnother={() => {
          sessionStorage.removeItem("coupon");
          navigate({ to: "/machine-type-selector-screen" });
        }}
        onFinish={() => navigate({ to: "/start-screen", reloadDocument: true })}
      />
    </Page>
  );
}

// today's invoice-question-screen rules
function canRequestInvoice(payment: PaymentType) {
  return (
    !getToken() &&
    payment.price > 0 &&
    !!getShopFromLocalStorage()?.canGenerateInvoices
  );
}
